/*
 * Builds the two things the dropped-suggestions dialog shows about a suggestion
 * that could not be applied: WHAT it would have written, and WHY it wasn't.
 *
 * Shared by the two moments a drop happens — the reveal-time hydration pass and
 * the accept-time pipeline — so a clinician sees identical wording either way.
 * The accept pipeline is a fixed dependency here: it reports a drop with only
 * `{ id, label, reason }`, so the host recovers the content from the record and
 * re-derives the reason on this side.
 */
import type { EditorOperation } from '@seen/ai-agents'
import type { StagedSuggestion } from '@seen/core/models/compositionNote'
import { effectiveSuggestionHtml } from '@seen/core/models/suggestionStagingBasic'
import type { Editor } from '@tiptap/react'
import { narrowPersistedOp } from '#src/components/rich-text-editor/aiSuggestionReconcile.ts'
import {
  type AnchorFailureKind,
  isConditionDropReason,
  suggestionOpContentHtml,
  unplaceableDropReason,
} from '#src/components/rich-text-editor/suggestionDropReasons.ts'
import {
  findUniqueTextRange,
  validateSuggestionContent,
} from '#src/contexts/CollaborativeEditorRegistry.tsx'
import { extractLinesFromHTML } from '#src/utils/html.ts'

/*
 * The suggestion's text as the clinician would have read it in the note.
 * Prefers the clinician's own island edit, since that — not the AI's original
 * op — is what an accept would have inserted. Flattened out of HTML so the row
 * shows prose and the copy button yields prose, never markup.
 */
export function droppedSuggestionContent(
  record: StagedSuggestion | undefined
): string | undefined {
  if (!record) {
    return undefined
  }
  const html = effectiveSuggestionHtml(
    record,
    suggestionOpContentHtml(narrowPersistedOp(record.op))
  )
  if (html === undefined) {
    return undefined
  }
  const text = extractLinesFromHTML(html)
  return text === '' ? undefined : text
}

/*
 * The text an op places against — the needle `findUniqueTextRange` resolves.
 * Undefined for the ops that target by other means (a heading, a field id, a
 * checklist label) or that don't target at all; none of those reach
 * `findUniqueTextRange`, so none can fail ambiguously.
 */
function anchorTextFor(op: EditorOperation): string | undefined {
  switch (op.type) {
    case 'replaceText':
      return op.findText
    case 'insertAfterText':
    case 'insertBeforeText':
    case 'replaceNode':
      return op.anchorText
    case 'appendToSection':
    case 'setChoiceById':
    case 'setTaskItemChecked':
    case 'insertAtEnd':
    case 'replaceAll':
    // A revision anchors on the proposal it replaces, never on text.
    case 'reviseProposal':
      return undefined
  }
}

/*
 * Re-ask the op's anchor question against the live document, so the copy can say
 * which way it failed. Undefined when the op doesn't anchor on text, and when the
 * anchor DOES resolve — for `replaceNode` that second case is the real signal
 * that the field, not the anchor, is what went missing.
 *
 * Re-asking rather than carrying the resolver's verdict forward is what lets the
 * reveal path and the accept path share one answer: the accept pipeline reports a
 * drop as `{ id, label, reason }` and has no channel for a discriminator, so a
 * threaded one would describe only half the drops.
 */
function anchorFailureFor(
  editor: Editor,
  op: EditorOperation
): AnchorFailureKind | undefined {
  const anchorText = anchorTextFor(op)
  if (anchorText === undefined) {
    return undefined
  }
  const match = findUniqueTextRange(editor, anchorText)
  return 'error' in match ? match.reason : undefined
}

/*
 * The clinician-facing reason a suggestion was dropped.
 *
 * `reportedReason` is whatever the accept pipeline forwarded. It is kept ONLY
 * when it is already clinician-facing (a condition validator's refusal);
 * anything else is a model-facing message naming the operation type, and is
 * replaced by copy derived from the op. The reveal path passes nothing and
 * always gets derived copy.
 */
export function clinicianDropReason({
  editor,
  record,
  noteSuggestionsEnabled,
  citationMentionsEnabled,
  reportedReason,
}: {
  editor: Editor
  record: StagedSuggestion | undefined
  noteSuggestionsEnabled: boolean
  citationMentionsEnabled: boolean
  reportedReason?: string
}): string {
  if (reportedReason !== undefined && isConditionDropReason(reportedReason)) {
    return reportedReason
  }
  const op = record ? narrowPersistedOp(record.op) : null
  /* Re-run the content gate to separate "this content can't go in a note" from
   * "there was nowhere to put it". Both arrive as an opaque technical string,
   * and only here is the editor available to ask again. */
  const html = effectiveSuggestionHtml(record, suggestionOpContentHtml(op))
  const contentIsUnsupported =
    op !== null &&
    html !== undefined &&
    validateSuggestionContent(
      editor,
      op.type,
      html,
      noteSuggestionsEnabled,
      citationMentionsEnabled
    ) !== null
  return unplaceableDropReason(
    op,
    contentIsUnsupported,
    op === null ? undefined : anchorFailureFor(editor, op)
  )
}
